import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type UIEvent,
} from 'react'
import { useStore } from 'jotai'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable, type FadingScrollableBottomChrome } from '../../../elements/FadingScrollable'
import { useActiveChatId, useChatMessages, useIsActiveTurn, useStableConversationKey } from '../../../../store'
import { isElectron } from '../../../../utils/runtimeUtils'
import { nextAutoScrollState, type ScrollIntentDirection } from '../../scroll-intent'
import { useScrollIntent } from '../../hooks/use-scroll-intent'
import { useLastTurnSpacer } from '../hooks/use-last-turn-spacer'
import {
  autoScrollAtom,
  MESSAGE_LIST_ELEMENT_ID,
  registerScrollFlagsRemeasure,
  useMessageListScrollActions,
  useMessageListScrollFlags,
} from '../scroll-atoms'
import { startCarryPin } from '../carry-pin'
import { RenderedMessages, type MessageListCallbacks } from './rendered-messages'

export type MessageListProps = MessageListCallbacks & {
  className?: string
  style?: CSSProperties
  bottomChrome?: FadingScrollableBottomChrome
}

// ---------------------------------------------------------------------------
// Scroller — the scroll container, its intent plumbing, and the follow pin
// ---------------------------------------------------------------------------

export const MessageListScroller = memo(function MessageListScroller({
  className,
  style,
  bottomChrome,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
}: MessageListProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  // FadingScrollable's ref surfaces `undefined` for a detached node; state
  // wants the `null` convention.
  const setContainerRef = useCallback((element: HTMLDivElement | null | undefined) => {
    setScrollElement(element ?? null)
  }, [])
  const { autoScroll } = useMessageListScrollFlags()
  const { setAutoScroll, setShowScrollToEnd } = useMessageListScrollActions()
  // Read inside the measurement rAF: the hysteresis band needs the value as
  // of that frame, not the one captured when the callback was created.
  const store = useStore()
  const activeChatId = useActiveChatId()
  // Per-chat resets compare against the swap-stable key: the temp→real
  // chat-id swap must not re-fire them mid-stream (they'd yank a user who
  // scrolled up during the first seconds of a new chat back to the bottom).
  const stableChatKey = useStableConversationKey(activeChatId)
  const messages = useChatMessages(activeChatId)

  useEffect(() => {
    return () => {
      setShowScrollToEnd(false)
    }
  }, [])

  // Anchoring the newest turn to the top edge is the spacer's job — see
  // `useLastTurnSpacer` for why sizing it correctly turns "pinned to the
  // bottom" into "last user message at the top".
  //
  // The index rides along because the id is *not* stable within a turn: the
  // stream's `metadata` event renames the optimistic user message to its
  // backend id. So the id addresses the DOM node, while anything that means
  // "a new turn started" keys on the index, which only moves when a turn is
  // actually appended.
  const { lastUserMessageId, lastUserMessageIndex } = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === 'user') {
        return { lastUserMessageId: messages[index].id, lastUserMessageIndex: index }
      }
    }
    return { lastUserMessageId: null, lastUserMessageIndex: -1 }
  }, [messages])
  const spacerRef = useLastTurnSpacer(scrollElement, lastUserMessageId, lastUserMessageIndex)

  // Sending re-engages auto-follow, so a reply composed after scrolling up
  // through the backlog still jumps to the message just sent rather than
  // streaming in off-screen. Not on the id: the mid-turn rename would read
  // as a second send and yank back a user who has since scrolled up.
  useEffect(() => {
    if (lastUserMessageIndex >= 0) setAutoScroll(true)
  }, [lastUserMessageIndex, setAutoScroll])

  // `autoScrollAtom` is module-scoped, so it outlives any one chat. Without
  // this reset a chat left scrolled up hands its `false` to the next chat
  // opened, which then mounts with follow disabled and silently refuses to
  // follow its own streaming reply. Reset per chat and let the restore
  // settle it: landing mid-history fires `handleScroll`, which turns
  // auto-follow back off on its own.
  useEffect(() => {
    setAutoScroll(true)
  }, [stableChatKey, setAutoScroll])

  // The carry pin (carry-pin.ts) — armed only while this chat's turn is
  // live. An idle chat's open-on-latest positioning belongs to the settle
  // pin in message-list/index.tsx, and gating on liveness is what keeps
  // these two scroll drivers from ever running at once.
  const isTurnLive = useIsActiveTurn()
  const hasMessages = messages.length > 0
  // The anchor id is read through a ref so the mid-turn rename never re-arms
  // the pin: a re-arm resets its takeover state (`lastPinnedTop`), and the
  // first pin() after a reset would write a scrollbar-scrolled-up user (no
  // input intent, so auto-follow is still engaged) back down to the anchor.
  // Layout effect, not render or passive effect: it must land after the
  // rename commit removes the old DOM id but before any ResizeObserver
  // delivery lets pin() re-query the anchor.
  const lastUserMessageIdRef = useRef(lastUserMessageId)
  useLayoutEffect(() => {
    lastUserMessageIdRef.current = lastUserMessageId
  }, [lastUserMessageId])
  useEffect(() => {
    if (!autoScroll || !scrollElement || !hasMessages || !isTurnLive) {
      return
    }

    return startCarryPin(
      scrollElement,
      () => {
        const anchorId = lastUserMessageIdRef.current
        return anchorId ? document.getElementById(`message-${anchorId}`) : null
      },
      { spacerEl: spacerRef.current },
    )
    // stableChatKey re-arms the observer on chat switch: the content wrapper
    // it observes can be remounted with the new chat's rows. The swap-stable
    // key, not activeChatId — the temp→real id swap must not re-arm the pin
    // mid-stream under a user who has scrolled up. The mid-turn rename of the
    // user message doesn't re-arm either (the id is read via the ref above),
    // so one pin instance carries its takeover state across the whole turn.
  }, [autoScroll, scrollElement, hasMessages, stableChatKey, isTurnLive])

  // Arm content-visibility on settled groups (settled-visibility.css) one
  // full painted frame after the chat's content renders. A row that has
  // never been laid out has no remembered size, so skipping it would lay it
  // out at the fallback intrinsic size and correct once scrolled near — the
  // flicker class Virtuoso was removed for. Double rAF: the first callback
  // can still precede the commit's own paint, the second is guaranteed to
  // run after one frame has painted (and recorded every row's real size).
  //
  // Keyed on `messages` because history can land in more than one commit
  // (optimistic cache, then the fetch), and a flag latched two frames after
  // the *first* batch would skip the later batches' rows on their very
  // first layout. Rows are a pure function of `messages`
  // (`RenderedMessages`), so re-arming per messages identity means the flag
  // only sets once the transcript has held still for a painted frame, rows
  // included. Once set it stays — the scroller remounts per chat (keyed on
  // the swap-stable key), so the flag resets with it.
  useEffect(() => {
    if (!scrollElement || !hasMessages || scrollElement.hasAttribute('data-cv-ready')) return
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        scrollElement.setAttribute('data-cv-ready', '')
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [scrollElement, hasMessages, messages])

  // Reading scrollHeight/clientHeight forces a synchronous layout. Both
  // callers below fire far more often than the flags they derive actually
  // change: the effect depends on `messages`, whose array identity changes on
  // every streaming token, and `handleScroll` runs on every scroll event. So
  // coalesce the measurement into a single rAF — at most one reflow per
  // frame instead of one per token or per event. (The atom writes themselves
  // are already cheap: jotai bails out when the value is unchanged.)
  const measureRafRef = useRef<number | null>(null)
  // Sticky across a coalesced frame: if a streaming-token measurement is
  // already scheduled when the user scrolls, the scroll's auto-scroll intent
  // must still be honoured. Dropping it would leave auto-follow engaged and
  // yank the user back to the bottom mid-read.
  const pendingIntentRef = useRef<ScrollIntentDirection | null>(null)
  const lastScrollHeightRef = useRef(0)
  const syncScrollFlags = useCallback(
    (container: HTMLElement | null, intent: ScrollIntentDirection | null) => {
      if (!container) return
      // 'up' wins a coalesced frame: if the user flicked up and back down
      // inside one frame, staying disengaged is the safe reading.
      if (intent && pendingIntentRef.current !== 'up') pendingIntentRef.current = intent
      if (measureRafRef.current !== null) return
      measureRafRef.current = requestAnimationFrame(() => {
        measureRafRef.current = null
        const pendingIntent = pendingIntentRef.current
        pendingIntentRef.current = null
        const scrollTop = container.scrollTop
        const scrollHeight = container.scrollHeight
        const distanceFromBottom = scrollHeight - scrollTop - container.clientHeight
        const contentGrew = scrollHeight > lastScrollHeightRef.current
        lastScrollHeightRef.current = scrollHeight
        setShowScrollToEnd(distanceFromBottom > 20)
        // Programmatic scrolls — the bottom pin's writes, an edge jump —
        // reach this path with no intent and must leave follow alone.
        // Upward intent was already applied on the input event, so only
        // the downward question is left to measure.
        if (pendingIntent === 'down') {
          setAutoScroll(
            nextAutoScrollState({ isFollowing: store.get(autoScrollAtom), distanceFromBottom, contentGrew }),
          )
        }
      })
    },
    [store, setAutoScroll, setShowScrollToEnd],
  )

  // Fires on the input event itself, ahead of the scroll it causes. Leaving
  // the bottom is always deliberate, so an upward flick disengages here and
  // needs no measurement at all — waiting a frame lets the bottom pin re-pin
  // and swallow the flick, which is the rubber-banding this fixes.
  //
  // Measuring from here too is not redundant with `handleScroll`: an input
  // event that can't move the list emits no scroll event, so this is what
  // lets a wheel-down while already sitting at the bottom — the "get me back
  // to live" gesture — re-engage follow.
  const recentScrollIntent = useScrollIntent(
    scrollElement,
    useCallback(
      (direction: ScrollIntentDirection) => {
        if (direction === 'up') setAutoScroll(false)
        syncScrollFlags(scrollElement, direction)
      },
      [scrollElement, setAutoScroll, syncScrollFlags],
    ),
  )

  useEffect(() => {
    return () => {
      if (measureRafRef.current !== null) cancelAnimationFrame(measureRafRef.current)
    }
  }, [])

  // Let components outside this scroller (e.g. a collapsing submessage
  // group) request a flags remeasure without reaching into the DOM.
  useEffect(() => {
    return registerScrollFlagsRemeasure(() => syncScrollFlags(scrollElement, null))
  }, [syncScrollFlags, scrollElement])

  // Check scroll position on new messages
  useEffect(() => {
    syncScrollFlags(scrollElement, null)
  }, [messages, scrollElement, syncScrollFlags])

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget
      if (!container) return
      // Momentum and inertia keep emitting `scroll` after the last input
      // event, so the whole window counts as the same user scroll.
      syncScrollFlags(container, recentScrollIntent())
    },
    [recentScrollIntent, syncScrollFlags],
  )

  return (
    <FadingScrollable
      id={MESSAGE_LIST_ELEMENT_ID}
      ref={setContainerRef}
      onScroll={handleScroll}
      className={twMerge('message-body relative min-h-64 flex-1 overflow-y-auto overscroll-y-contain', className)}
      style={style}
      fadeSize={isElectron ? '24px' : '10%'}
      bottomChrome={bottomChrome}
    >
      <RenderedMessages
        messages={messages}
        onStagedActionClick={onStagedActionClick}
        onStagedActionCreated={onStagedActionCreated}
        onReminderClick={onReminderClick}
      />
      {/* Height owned by `useLastTurnSpacer`; no style prop, or React
            would clobber the value it writes. */}
      <div ref={spacerRef} className="shrink-0" aria-hidden="true" />
    </FadingScrollable>
  )
})
